import { Fragment, type NodeType } from '@atlaskit/editor-prosemirror/model';
import type { EditorState } from '@atlaskit/editor-prosemirror/state';
import { fg } from '@atlaskit/platform-feature-flags';
/**
 * When the startPos resolves to the beginning of a text node inside a paragraph
 * or heading, and the replacement fragment's first child is the same block type
 * with block-level marks (e.g. alignment), replaceRange will only merge the
 * inline content — losing the block-level marks/attrs from the replacement node.
 *
 * This adjustment decreases startPos by 1 so it points to the block node itself,
 * allowing replaceRange to replace the entire node (including block-level marks
 * like alignment).
 *
 * If any marks on the replacement node are invalid in the current schema context,
 * ProseMirror will strip them automatically during replaceRange.
 */
export function calculateBlockMarkAdjustment({
	state,
	fragment,
	startPos,
	userContentEndPos,
}: {
	fragment: Fragment;
	startPos: number;
	state: EditorState;
	userContentEndPos: number;
}): PositionAdjustmentResult {
	const bailout = { startPos, userContentEndPos, adjusted: false };

	if (!fg('platform_editor_ai_block_mark_pos_fix')) {
		return bailout;
	}

	const { paragraph, heading } = state.schema.nodes;

	// Validate positions
	if (startPos < 1 || startPos > state.doc.content.size) {
		return bailout;
	}

	const $start = state.doc.resolve(startPos);
	const parentType = $start.parent.type;

	// Check if startPos is at offset 0 inside a paragraph or heading (i.e. at the
	// start of the text content within the block node)
	const isInParagraph = paragraph && parentType === paragraph;
	const isInHeading = heading && parentType === heading;
	if ((!isInParagraph && !isInHeading) || $start.parentOffset !== 0) {
		return bailout;
	}

	// Check that the replacement fragment's first child is the same block type
	const firstChild = fragment.firstChild;
	if (!firstChild || firstChild.type !== parentType) {
		return bailout;
	}

	// Only adjust when the replacement carries block-level marks — without this
	// guard the adjustment fires on every same-type streaming replacement,
	// breaking discard/undo position tracking. If any marks are invalid in the
	// current schema context, ProseMirror will strip them during replaceRange.
	if (!firstChild.marks || firstChild.marks.length === 0) {
		return bailout;
	}

	// Verify the position before is actually the block node's opening position
	if ($start.depth < 1 || $start.before($start.depth) !== startPos - 1) {
		return bailout;
	}

	return {
		startPos: startPos - 1,
		userContentEndPos,
		adjusted: true,
	};
}

export interface PositionAdjustmentResult {
	adjusted: boolean;
	startPos: number;
	userContentEndPos: number;
}

/**
 * Pure version of adjustStartEndPosForEmptySelection
 */
export function calculateEmptySelectionAdjustment({
	state,
	fragment,
	startPos,
	userContentEndPos,
}: {
	fragment: Fragment;
	startPos: number;
	state: EditorState;
	userContentEndPos: number;
}): PositionAdjustmentResult {
	const { paragraph, listItem, taskItem, decisionItem } = state.schema.nodes;
	const bailout = { startPos, userContentEndPos, adjusted: false };

	// Validate positions
	if (startPos < 0 || startPos > state.doc.content.size) {
		return bailout;
	}

	const $start = state.doc.resolve(startPos);
	const startParent = $start.parent;

	const inList =
		startParent.type === paragraph &&
		$start.depth > 1 &&
		$start.node($start.depth - 1)?.type === listItem;
	const inTaskItem = taskItem && startParent.type === taskItem;
	const inDecisionItem = decisionItem && startParent.type === decisionItem;

	if (!inList && !inTaskItem && !inDecisionItem) {
		return bailout;
	}

	const $end = state.doc.resolve(userContentEndPos);
	const endParent = $end.parent;

	const newNode = fragment.firstChild;
	const oneBlock = fragment.childCount === 1 && newNode && newNode.isBlock;
	const newStartBlock = oneBlock && newNode.type.name !== startParent.type.name;
	const newEndBlock = oneBlock && newNode.type.name !== endParent.type.name;

	let startPosAdjusted = startPos;
	let endPosAdjusted = userContentEndPos;

	if (newStartBlock) {
		for (let depth = $start.depth, diff = 1; depth > 0; depth--, diff++, startPosAdjusted--) {
			const ancestor = $start.node(depth);
			if (ancestor.type.contentMatch.matchType(newNode.type)) {
				break;
			}
			const before = $start.before(depth);
			if (before + diff !== startPos) {
				break;
			}
		}
	}

	if (newEndBlock) {
		for (let depth = $end.depth, diff = 1; depth > 0; depth--, diff++, endPosAdjusted++) {
			const ancestor = $end.node(depth);
			if (ancestor.type.contentMatch.matchType(newNode.type)) {
				break;
			}
			const after = $end.after(depth);
			if (after - diff !== userContentEndPos) {
				break;
			}
		}
	}

	if (startPosAdjusted !== bailout.startPos || endPosAdjusted !== bailout.userContentEndPos) {
		const pos =
			endPosAdjusted - userContentEndPos >= startPos - startPosAdjusted
				? endPosAdjusted
				: startPosAdjusted;

		return { startPos: pos, userContentEndPos: pos, adjusted: true };
	}

	return bailout;
}

/**
 * Pure version of adjustStartEndPosForRangeSelection
 */
export function calculateRangeSelectionAdjustment({
	state,
	fragment,
	startPos,
	userContentEndPos,
}: {
	fragment: Fragment;
	startPos: number;
	state: EditorState;
	userContentEndPos: number;
}): PositionAdjustmentResult {
	const { paragraph, tableCell, tableHeader } = state.schema.nodes;
	const bailout = { startPos, userContentEndPos, adjusted: false };

	// Validate positions
	if (startPos < 0 || startPos > state.doc.content.size) {
		return bailout;
	}

	let $start = state.doc.resolve(startPos);

	if ($start.depth > 0 && $start.before() !== startPos - 1) {
		return bailout;
	}

	const types: NodeType[] = [];
	let node = fragment.firstChild;
	while (node && node.firstChild) {
		types.push(node.type);
		node = node.firstChild;
	}
	types.reverse();

	if (types.length === 1 && types[0] === paragraph) {
		return bailout;
	}

	if (fg('platform_editor_ai_replace_table_cell_issue_fix')) {
		/**
		 * If start pos is in a table cell or table header then bailout
		 * 	because adjustments for table cells are handled separately
		 * 	in calculateTableAdjustment.
		 */
		const startPosNodeType = $start.parent.type;
		if (startPosNodeType === tableCell || startPosNodeType === tableHeader) {
			return bailout;
		}
	}

	let startPosAdjusted = startPos;
	node = $start.parent.firstChild;
	while (node && node.firstChild) {
		startPosAdjusted++;
		node = node.firstChild;
	}

	$start = state.doc.resolve(startPosAdjusted);
	for (let i = 0, depth = $start.depth; depth > 0; i++, depth--, startPosAdjusted--) {
		const ancestor = $start.node(depth);
		if (ancestor.type !== types[i]) {
			break;
		}
		const before = $start.before(depth);
		if (before + 1 !== startPosAdjusted) {
			break;
		}
	}

	if (startPosAdjusted < bailout.startPos) {
		return {
			startPos: startPosAdjusted,
			userContentEndPos: bailout.userContentEndPos,
			adjusted: true,
		};
	}

	return bailout;
}

/**
 * Returns true when `startPos` is a valid spot to perform the
 * "convert list -> task list" position adjustment, i.e.:
 *
 *  1. There is a `listItem` ancestor in the chain.
 *  2. That listItem's parent is a bullet/ordered list, and it is the FIRST
 *     bullet/ordered list in the ancestor chain (i.e. NOT inside a nested
 *     list).
 *  3. The listItem is the FIRST child of its parent list.
 *  4. `startPos` is at the very beginning of the listItem's first paragraph.
 *
 * Conditions (1) & (2) replace the previous `isTopLevelListBeingReplaced`
 * check. Conditions (3) & (4) replace the previous `$start.parentOffset === 0`
 * check, which incorrectly fired for any listItem (2nd, 3rd, …).
 *
 * Examples that return TRUE:
 *  - bulletList -> listItem(idx 0) -> paragraph<startPos>text
 *  - orderedList -> listItem(idx 0) -> paragraph<startPos>
 *
 * Examples that return FALSE:
 *  - bulletList -> listItem(idx 0) -> bulletList -> ...           (nested list)
 *  - bulletList -> listItem(idx 1+) -> paragraph<startPos>...     (non-first item)
 *  - bulletList -> listItem(idx 0) -> paragraph -> text<startPos> (mid-text)
 */
function isStartAtFirstItemOfTopLevelList(state: EditorState, startPos: number): boolean {
	const { bulletList, orderedList, listItem } = state.schema.nodes;
	const $start = state.doc.resolve(startPos);

	let listItemDepth = -1; // depth of the nearest (innermost) listItem ancestor
	let listsSeen = 0; // count of bullet/ordered list ancestors

	for (let depth = $start.depth; depth > 0; depth--) {
		const node = $start.node(depth);

		// Track only the nearest (innermost) listItem.
		if (node.type === listItem && listItemDepth === -1) {
			listItemDepth = depth;
			continue;
		}

		if (node.type === bulletList || node.type === orderedList) {
			listsSeen++;
			if (listsSeen > 1) {
				// Nested list — not top-level.
				return false;
			}
		}
	}

	// No listItem ancestor at all, or no enclosing list -> not applicable.
	if (listItemDepth === -1 || listsSeen === 0) {
		return false;
	}

	// The list that directly contains the nearest listItem must be a
	// bullet/ordered list, and that listItem must be its FIRST child.
	const parentList = $start.node(listItemDepth - 1);
	if (parentList.type !== bulletList && parentList.type !== orderedList) {
		return false;
	}
	if ($start.index(listItemDepth - 1) !== 0) {
		return false;
	}

	// startPos must be at the very beginning of the listItem's first paragraph.
	// $start.start(listItemDepth) is the position just inside the listItem;
	// +1 skips the opening token of the first child paragraph.
	const firstParagraphStart = $start.start(listItemDepth) + 1;
	return startPos <= firstParagraphStart;
}

/**
 * Do position adjustments only when task list is replacing top level list.
 * When user has explictily asked to convert list to task list,
 * 	startPos must be at the beginning of the FIRST list item of the top-level
 * 	bullet/ordered list (NOT a nested list, NOT a non-first item, NOT mid-text).
 * @param fragment
 * @param startPos
 * @param state
 * @returns true if position adjustments are needed.
 */
export function shouldAdjustForReplaceListWithTaskList({
	fragment,
	startPos,
	state,
}: {
	fragment: Fragment;
	startPos: number;
	state: EditorState;
}): boolean {
	const { taskList } = state.schema.nodes;
	const isFirstChildTaskList = fragment.firstChild?.type === taskList;
	return isFirstChildTaskList && isStartAtFirstItemOfTopLevelList(state, startPos);
}
/**
 * Pure version of adjustStartAndUserContentEndPosForList
 */
export function calculateListAdjustment({
	state,
	fragment,
	startPos,
	userContentEndPos,
}: {
	fragment: Fragment;
	startPos: number;
	state: EditorState;
	userContentEndPos: number;
}): PositionAdjustmentResult {
	const { listItem, paragraph } = state.schema.nodes;
	const bailout = { startPos, userContentEndPos, adjusted: false };

	if (!listItem) {
		return bailout;
	}

	const doc = state.doc;
	const docSize = doc.content.size;

	// Validate positions
	if (startPos < 0 || startPos > docSize || userContentEndPos < 0 || userContentEndPos > docSize) {
		return bailout;
	}

	let startPosAdjusted = startPos;
	let userContentEndPosAdjusted = userContentEndPos;

	const fragmentFirstNode = fragment.firstChild;
	if (!fragmentFirstNode) {
		return bailout;
	}
	/**
	 * If first child can be child of list item then no need to adjust positions.
	 * In case of table, layout or panel, first child won't be valid content of list item,
	 * 	thus adjustments will happen to avoid dangling list items.
	 */
	const firstChildFragment = Fragment.from(fragmentFirstNode);
	const isFirstValidListItemContent = listItem?.validContent(firstChildFragment);
	/**
	 * Task list is valid child of listItem.
	 * We have observed that user rarely use task item in list item.
	 * So most of the time when user want to replace
	 * 	list with task list.
	 * So if first child is task list then should do adjustments.
	 */
	let shouldAdjustPositions = false;
	if (fg('platform_editor_ai_replace_list_with_tasklist')) {
		shouldAdjustPositions =
			!isFirstValidListItemContent ||
			shouldAdjustForReplaceListWithTaskList({
				fragment,
				startPos,
				state,
			});
	} else {
		shouldAdjustPositions = !isFirstValidListItemContent;
	}
	if (!shouldAdjustPositions) {
		return bailout;
	}

	const $start = doc.resolve(startPos);
	let curParent = $start.node($start.depth);
	let curParentParent = $start.depth > 1 ? $start.node($start.depth - 1) : null;

	if (
		curParent.type === paragraph &&
		$start.parentOffset === 0 &&
		curParentParent?.type === listItem
	) {
		startPosAdjusted -= 2;
	} else if (curParent.type === listItem) {
		startPosAdjusted -= 1;
	}

	const $userContentEnd = doc.resolve(userContentEndPos);
	curParent = $userContentEnd.node($userContentEnd.depth);
	curParentParent =
		$userContentEnd.depth > 1 ? $userContentEnd.node($userContentEnd.depth - 1) : null;

	if (
		curParent.type === paragraph &&
		$userContentEnd.parentOffset === curParent.content.size &&
		curParentParent?.type === listItem
	) {
		userContentEndPosAdjusted += 2;
	} else if (curParent.type === listItem) {
		userContentEndPosAdjusted += 1;
	}

	// Clamp to valid range
	startPosAdjusted = Math.max(0, startPosAdjusted);
	userContentEndPosAdjusted = Math.min(docSize, userContentEndPosAdjusted);

	const adjusted = startPosAdjusted !== startPos || userContentEndPosAdjusted !== userContentEndPos;

	return { startPos: startPosAdjusted, userContentEndPos: userContentEndPosAdjusted, adjusted };
}
